import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserProfileById } from "../services/profile";
import { Student, Experience, Project, Contest, Misc } from "../types";
import { generateLogoLinkUrl } from "../services/brandfetch";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import WorkIcon from "@mui/icons-material/WorkOutline";
import CodeIcon from "@mui/icons-material/Code";
import EmojiEventsIcon from "@mui/icons-material/EmojiEventsOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<Student | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setError("User ID is missing.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        console.log(`[UserProfile] Fetching data for user ID: ${userId}`);
        const userData = await getUserProfileById(Number(userId));
        console.log("[UserProfile] User data received:", userData);
        setUser(userData);
      } catch (err) {
        console.error(
          `[UserProfile] Failed to fetch user data for ID ${userId}:`,
          err
        );
        const message =
          err instanceof Error ? err.message : "An unknown error occurred.";
        setError(`Failed to load user profile: ${message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId]);

  const goBack = () => {
    navigate(-1);
  };

  const renderDetailList = (
    title: string,
    items: Experience[] | Project[] | Contest[] | Misc[] | undefined,
    primaryField: string,
    secondaryField?: string
  ) => {
    if (!items || items.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={{
            fontStyle: "italic",
            ml: 1,
            color: "rgba(51, 51, 51, 0.5)",
            fontWeight: 300,
          }}
        >
          None added.
        </Typography>
      );
    }
    return (
      <List dense disablePadding sx={{ mb: 1, pl: 1 }}>
        {items.map((item, index) => {
          let logoUrl: string | null = null;
          if (title === "Experience" && (item as Experience).company_url) {
            try {
              const fullUrl = (item as Experience).company_url as string;
              const urlWithProto = fullUrl.startsWith("http")
                ? fullUrl
                : `https://${fullUrl}`;
              const urlObject = new URL(urlWithProto);
              const domain = urlObject.hostname.replace(/^www\./, "");
              if (domain) {
                logoUrl = generateLogoLinkUrl(domain, {
                  type: "icon",
                  height: 24,
                });
                console.log(
                  `[UserProfile] Logo URL from company_url (${domain}): ${logoUrl}`
                );
              }
            } catch (e) {
              console.warn(
                `[UserProfile] Invalid company URL for item ${item.id}:`,
                (item as Experience).company_url,
                e
              );
            }
          }

          return (
            <ListItem
              key={item.id || index}
              disableGutters
              sx={{ py: 0.25, display: "flex", alignItems: "center" }}
            >
              {logoUrl && (
                <Box
                  component="img"
                  src={logoUrl}
                  alt={`${(item as Experience).company ?? "Company"} logo`}
                  sx={{
                    height: 24,
                    width: "auto",
                    mr: 1.5,
                    verticalAlign: "middle",
                    flexShrink: 0,
                    objectFit: "contain",
                  }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              <ListItemText
                primary={item[primaryField as keyof typeof item]}
                secondary={
                  secondaryField
                    ? item[secondaryField as keyof typeof item]
                    : null
                }
                primaryTypographyProps={{
                  variant: "body2",
                  sx: { color: "#333333", fontWeight: 300 },
                }}
                secondaryTypographyProps={{
                  variant: "caption",
                  sx: { color: "rgba(51, 51, 51, 0.7)", fontWeight: 300 },
                }}
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderSection = (
    title: string,
    IconComponent: React.ElementType,
    content: React.ReactNode
  ) => {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <IconComponent
            sx={{ fontSize: "1rem", color: "rgba(51, 51, 51, 0.7)" }}
          />
          <Typography
            variant="h6"
            component="h3"
            sx={{
              color: "rgba(51, 51, 51, 0.7)",
              fontSize: "0.875rem",
              fontWeight: 300,
              letterSpacing: "0.025em",
              textTransform: "lowercase",
            }}
          >
            {title}
          </Typography>
        </Box>
        {content}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "#f8f8f8",
      }}
    >
      <Box
        sx={{
          py: 3,
          px: 4,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
        }}
      >
        <IconButton
          edge="start"
          onClick={goBack}
          aria-label="back"
          sx={{
            mr: 1,
            color: "#333333",
            "&:hover": {
              bgcolor: "transparent",
              color: "rgba(51, 51, 51, 0.7)",
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1.25rem" }} />
        </IconButton>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            flexGrow: 1,
            fontSize: { xs: "1.25rem", sm: "1.25rem" },
            fontWeight: 300,
            letterSpacing: "0.05em",
            color: "#333333",
            textTransform: "lowercase",
          }}
        >
          user profile
        </Typography>
      </Box>

      <Container
        maxWidth="md"
        sx={{
          flexGrow: 1,
          py: 4,
          px: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress sx={{ color: "rgba(51, 51, 51, 0.4)" }} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ m: { xs: 1, sm: 2 } }}>
            {error}
          </Alert>
        )}
        {!loading && !error && user && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              maxWidth: "540px",
              width: "100%",
              borderRadius: 0,
              border: "1px solid rgba(51, 51, 51, 0.2)",
              maxHeight: "540px",
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(51, 51, 51, 0.2)",
                "&:hover": {
                  backgroundColor: "rgba(51, 51, 51, 0.4)",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                mb: 3,
              }}
            >
              <Avatar
                src={user.avatar_url ?? undefined}
                alt={user.name}
                sx={{
                  width: 48,
                  height: 48,
                  border: "1px solid rgba(51, 51, 51, 0.2)",
                }}
              >
                {user.name ? user.name.charAt(0).toUpperCase() : "?"}
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontSize: "1.25rem",
                    color: "#333333",
                    fontWeight: 300,
                    letterSpacing: "0.025em",
                    textTransform: "lowercase",
                  }}
                >
                  {user.name}
                </Typography>
                {/* <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.875rem",
                    color: "rgba(51, 51, 51, 0.7)",
                    fontWeight: 300,
                  }}
                >
                  {user.email}
                </Typography> */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    color: "rgba(51, 51, 51, 0.5)",
                    fontWeight: 300,
                  }}
                >
                  class: {user.class_year ?? "n/a"} | position:{" "}
                  {user.position ?? "n/a"} | elo: {user.elo}
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                my: 2,
                bgcolor: "rgba(51, 51, 51, 0.2)",
                border: "none",
                height: "1px",
              }}
            />

            {renderSection(
              "experience",
              WorkIcon,
              renderDetailList(
                "Experience",
                user.experiences,
                "title",
                "company"
              )
            )}
            {renderSection(
              "contests",
              EmojiEventsIcon,
              renderDetailList("Contests", user.contests, "name", "organizer")
            )}
            {renderSection(
              "projects",
              CodeIcon,
              renderDetailList("Projects", user.projects, "name", "description")
            )}
            {renderSection(
              "other info",
              InfoIcon,
              renderDetailList("Other Info", user.misc, "key", "value")
            )}
          </Paper>
        )}
        {!loading && !error && !user && (
          <Typography
            sx={{
              textAlign: "center",
              mt: 4,
              color: "rgba(51, 51, 51, 0.7)",
              fontWeight: 300,
            }}
          >
            user not found.
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default UserProfile;
